// FILE: src/components/EventsMember.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

import {
  getAllEvents,
  rsvpToEvent,
  checkInToEvent,
} from "../services/eventsService";

/* ---------- Helpers ---------- */

function toJsDate(startAt) {
  if (!startAt) return null;
  if (startAt.toDate) return startAt.toDate();
  if (typeof startAt.seconds === "number") return new Date(startAt.seconds * 1000);
  return new Date(startAt);
}

function safeUrl(value) {
  try {
    if (!value) return "";
    const u = new URL(value);
    return u.toString();
  } catch {
    return "";
  }
}

function isSameDay(a, b) {
  const d1 = toJsDate(a);
  const d2 = toJsDate(b);
  if (!d1 || !d2) return false;
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function formatDateTime(ev) {
  const d = toJsDate(ev?.startAt);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isUpcoming(ev) {
  const d = toJsDate(ev?.startAt);
  if (!d || Number.isNaN(d.getTime())) return false;
  return d.getTime() >= Date.now();
}

/* ---------- UI Bits ---------- */

function DateBox({ dateValue }) {
  const d = toJsDate(dateValue);
  if (!d || Number.isNaN(d.getTime())) return null;

  const day = d.getDate();
  const month = d.toLocaleString("default", { month: "short" });
  const weekday = d.toLocaleString("default", { weekday: "short" });

  return (
    <div className="flex flex-col items-center justify-center w-14 h-16 rounded-xl bg-indigo-50 text-indigo-700 shadow-sm border border-indigo-100">
      <span className="text-[10px] uppercase tracking-wide">{weekday}</span>
      <span className="text-xs font-semibold leading-tight">{month}</span>
      <span className="text-lg font-bold leading-none">{day}</span>
    </div>
  );
}

function EventMap({ location }) {
  if (!location) return null;
  const src = `https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed`;
  return (
    <div className="mt-3 rounded-lg overflow-hidden border">
      <iframe
        title={`Map for ${location}`}
        src={src}
        width="100%"
        height="160"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

function Poster({ url, title = "Event" }) {
  const [expanded, setExpanded] = useState(false);
  const posterUrl = safeUrl(url);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expanded]);

  if (!posterUrl) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="block w-full rounded-lg border bg-slate-50 p-2 text-left cursor-zoom-in"
        aria-label={`Enlarge poster for ${title}`}
        title="Click to enlarge poster"
      >
        <img
          src={posterUrl}
          alt={`${title} poster`}
          className="w-full h-auto object-contain rounded-md"
          loading="lazy"
        />
        <span className="block mt-2 text-center text-xs text-indigo-700">
          Click poster to enlarge or save
        </span>
      </button>

      {expanded && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${title} poster preview`}
          onClick={() => setExpanded(false)}
        >
          <div className="w-full max-w-5xl flex justify-end gap-3 mb-3">
            <a
              href={posterUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="px-4 py-2 bg-white rounded-lg text-sm font-medium text-slate-900"
              title="Open original poster to save or zoom"
            >
              Open original / Save
            </a>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="px-4 py-2 bg-white rounded-lg text-sm font-medium text-slate-900"
              aria-label="Close poster preview"
            >
              Close ×
            </button>
          </div>
          <img
            src={posterUrl}
            alt={`${title} poster enlarged`}
            className="max-w-full max-h-[82vh] object-contain rounded-lg"
            onClick={(event) => event.stopPropagation()}
          />
          <p className="text-white/80 text-xs mt-3 text-center">
            Open original to zoom further or save the image to your device.
          </p>
        </div>
      )}
    </>
  );
}

function JoinOnline({ meetingUrl }) {
  const link = safeUrl(meetingUrl);
  if (!link) return null;
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center justify-center px-3 py-1 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700"
    >
      Join Online
    </a>
  );
}

/* ---------- Component ---------- */

export default function EventsMember() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [busyEventId, setBusyEventId] = useState(null);

  const [profile, setProfile] = useState(null);
  const [myRsvpEventIds, setMyRsvpEventIds] = useState(new Set());
  const [myCheckinEventIds, setMyCheckinEventIds] = useState(new Set());

  const [guestEvent, setGuestEvent] = useState(null);
  const [guestForm, setGuestForm] = useState({fullName:"",email:"",phone:"",inviteCode:""});
  const [guestBusy, setGuestBusy] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [authPromptMessage, setAuthPromptMessage] = useState("");

  const openAuthPrompt = (msg) => {
    setAuthPromptMessage(msg);
    setAuthPromptOpen(true);
  };

  const closeAuthPrompt = () => {
    setAuthPromptOpen(false);
    setAuthPromptMessage("");
  };

  const loadEvents = async () => {
    try {
      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load events", err);
      setEvents([]);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        setProfile(snap.exists() ? snap.data() : null);
      } catch (err) {
        console.error("Failed to load user profile:", err);
      }
    };
    loadProfile();
  }, [user]);

  useEffect(() => {
    const loadMyRsvps = async () => {
      if (!user) {
        setMyRsvpEventIds(new Set());
        return;
      }

      const ids = new Set();
      for (const ev of events) {
        try {
          const rRef = doc(db, "events", ev.id, "rsvps", user.uid);
          // eslint-disable-next-line no-await-in-loop
          const rSnap = await getDoc(rRef);
          if (rSnap.exists()) ids.add(ev.id);
        } catch {
          // ignore
        }
      }
      setMyRsvpEventIds(ids);
    };

    loadMyRsvps();
  }, [user, events]);

  useEffect(() => {
    const loadMyCheckins = async () => {
      if (!user) {
        setMyCheckinEventIds(new Set());
        return;
      }

      const ids = new Set();
      for (const ev of events) {
        try {
          const cRef = doc(db, "events", ev.id, "checkins", user.uid);
          // eslint-disable-next-line no-await-in-loop
          const cSnap = await getDoc(cRef);
          if (cSnap.exists()) ids.add(ev.id);
        } catch {
          // ignore
        }
      }
      setMyCheckinEventIds(ids);
    };

    loadMyCheckins();
  }, [user, events]);

  const hasRsvped = useCallback(
    (ev) => {
      if (!user) return false;
      return myRsvpEventIds.has(ev.id);
    },
    [user, myRsvpEventIds]
  );

  const alreadyCheckedIn = useCallback(
    (ev) => {
      if (!user) return false;
      return myCheckinEventIds.has(ev.id);
    },
    [user, myCheckinEventIds]
  );

  const greetingName =
    profile?.firstName ||
    user?.displayName ||
    (user?.email ? user.email.split("@")[0] : "there");

  const rsvp = async (ev) => {
    if (!user) {setGuestEvent(ev); return;}
    if (hasRsvped(ev)) return alert("You already RSVPed.");

    let inviteCode = "";
    if (ev.requireInviteCode) {
      inviteCode = window.prompt("Enter invite code for this event:") || "";
      inviteCode = inviteCode.trim();
      if (!inviteCode) return alert("Invite code is required for this event.");
    }

    const email = (user.email || "").trim();
    if (!email) {
      alert("Your account has no email address. Please update your account before registering.");
      return;
    }

    const profileName = [profile?.firstName, profile?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    let fullName = (profileName || profile?.fullName || user.displayName || "").trim();
    if (!fullName) {
      fullName = (window.prompt("Please enter your full name for this RSVP:") || "").trim();
      if (!fullName) return;
    }

    try {
      setBusyEventId(ev.id);

      await rsvpToEvent({
        eventId: ev.id,
        status: "going",
        inviteCode,
        fullName,
        email,
      });

      setMyRsvpEventIds((prev) => {
        const next = new Set(prev);
        next.add(ev.id);
        return next;
      });

      alert("RSVP successful!");
      await loadEvents();
    } catch (err) {
      console.error("RSVP failed:", err);

      const rawMsg = String(err?.message || "").toLowerCase();

      if (rawMsg.includes("already rsvped")) {
        alert("You already RSVPed.");
      } else if (
        rawMsg.includes("invalid invite code") ||
        rawMsg.includes("invite code not found")
      ) {
        alert("Invalid invite code.");
      } else if (rawMsg.includes("disabled")) {
        alert("This invite code is disabled.");
      } else if (
        ev.requireInviteCode && (
          rawMsg.includes("limit") ||
          rawMsg.includes("max") ||
          rawMsg.includes("capacity")
        )
      ) {
        alert("This invite code has reached its RSVP limit.");
      } else if (err?.code === "permission-denied") {
        alert("RSVP blocked by Firestore rules. Deploy the updated firestore.rules.");
      } else {
        alert(err?.message || "RSVP failed.");
      }
    } finally {
      setBusyEventId(null);
    }
  };

  const submitGuestRsvp = async (e) => {
    e.preventDefault();
    if (!guestEvent) return;
    setGuestBusy(true);
    try {
      await rsvpToEvent({eventId: guestEvent.id, status: 'going', ...guestForm});
      alert('RSVP successful! A confirmation email has been queued.');
      setGuestEvent(null);
      setGuestForm({fullName:'',email:'',phone:'',inviteCode:''});
    } catch (err) {
      alert(err?.message || 'Guest RSVP failed.');
    } finally {setGuestBusy(false);}
  };

  const checkIn = async (ev) => {
    if (!user) return openAuthPrompt("You need an account to check in.");

    if (ev.isOnline) return alert("Online events don’t require check-in.");

    if (!ev.isSundayService && !hasRsvped(ev)) {
      return alert("You must RSVP before checking in.");
    }

    if (!isSameDay(ev.startAt, new Date())) {
      return alert("You can only check in on the event day.");
    }

    if (alreadyCheckedIn(ev)) return alert("You already checked in.");

    try {
      setBusyEventId(ev.id);
      await checkInToEvent(ev.id);

      setMyCheckinEventIds((prev) => {
        const next = new Set(prev);
        next.add(ev.id);
        return next;
      });

      alert("Checked in!");
      await loadEvents();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Check-in failed.");
    } finally {
      setBusyEventId(null);
    }
  };

  const upcomingEvents = useMemo(() => {
    return events
      .filter((ev) => isUpcoming(ev))
      .sort((a, b) => {
        const da = toJsDate(a?.startAt)?.getTime() ?? 0;
        const dbb = toJsDate(b?.startAt)?.getTime() ?? 0;
        return da - dbb;
      });
  }, [events]);

  const previousEvents = useMemo(() => {
    if (!user) return [];
    return events
      .filter((ev) => !isUpcoming(ev))
      .sort((a, b) => {
        const da = toJsDate(a?.startAt)?.getTime() ?? 0;
        const dbb = toJsDate(b?.startAt)?.getTime() ?? 0;
        return dbb - da;
      });
  }, [events, user]);

  return (
    <>
      <div className="p-4 max-w-3xl mx-auto space-y-8">
        <h2 className="text-2xl font-bold">Upcoming Events</h2>

        <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-100 shadow-sm text-sm">
          <div className="text-xs uppercase tracking-wide text-indigo-500 mb-1">
            Welcome
          </div>
          <div className="font-semibold text-indigo-900 mb-1">
            Hi {greetingName}, here are the upcoming events:
          </div>
          <p className="text-indigo-900/90">RSVP to get reminders and updates.</p>
        </div>

        <section className="space-y-3">
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-gray-600">No upcoming events yet.</p>
          ) : (
            <ul className="space-y-4">
              {upcomingEvents.map((ev) => (
                <li
                  key={ev.id}
                  className="p-4 border rounded shadow-sm bg-white flex flex-col gap-4"
                >
                  <Poster url={ev.posterUrl} title={ev.title || "Event"} />
                  <div className="flex items-start gap-4">
                    <DateBox dateValue={ev.startAt} />

                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-lg">{ev.title || "Event"}</div>
                        {ev.isOnline && (
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] rounded-full">
                            Online
                          </span>
                        )}
                        {ev.requireInviteCode && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full">
                            Invite code
                          </span>
                        )}
                      </div>

                      <div className="text-gray-600 text-sm">{formatDateTime(ev)}</div>

                      {!ev.isOnline && ev.location && (
                        <div className="text-xs text-gray-500 mt-1">📍 {ev.location}</div>
                      )}

                      {ev.description && (
                        <p className="mt-2 text-sm text-gray-700">{ev.description}</p>
                      )}

                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          onClick={() => rsvp(ev)}
                          disabled={busyEventId === ev.id || hasRsvped(ev)}
                          className="px-3 py-1 border rounded bg-indigo-50 text-indigo-700 text-sm disabled:opacity-60"
                        >
                          {hasRsvped(ev)
                            ? "RSVPed"
                            : busyEventId === ev.id
                            ? "Please wait..."
                            : "RSVP"}
                        </button>

                        {ev.isOnline &&
                          (ev.meetingUrl ? (
                            <JoinOnline meetingUrl={ev.meetingUrl} />
                          ) : (
                            <span className="text-xs text-gray-500 self-center">
                              Online link not added yet.
                            </span>
                          ))}

                        {!ev.isOnline && (
                          <button
                            onClick={() => checkIn(ev)}
                            disabled={busyEventId === ev.id}
                            className="px-3 py-1 border rounded bg-green-50 text-green-700 text-sm disabled:opacity-60"
                          >
                            {busyEventId === ev.id ? "Please wait..." : "Check-In"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {!ev.isOnline && ev.location && <EventMap location={ev.location} />}
                </li>
              ))}
            </ul>
          )}
        </section>

        {user && (
          <section className="space-y-3">
            <h3 className="text-lg font-semibold">Previous Events</h3>

            {previousEvents.length === 0 ? (
              <p className="text-sm text-gray-600">No previous events yet.</p>
            ) : (
              <ul className="space-y-3">
                {previousEvents.slice(0, 6).map((ev) => (
                  <li
                    key={ev.id}
                    className="p-4 border rounded bg-white flex items-start gap-4"
                  >
                    <DateBox dateValue={ev.startAt} />
                    <div className="flex-1">
                      <div className="font-semibold">{ev.title || "Event"}</div>
                      <div className="text-gray-600 text-sm">{formatDateTime(ev)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {guestEvent && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitGuestRsvp} className="bg-white rounded-xl p-6 w-full max-w-md space-y-3 shadow-xl">
            <h3 className="text-lg font-bold">Guest RSVP: {guestEvent.title}</h3>
            <p className="text-sm text-gray-600">No account is required.</p>
            <label className="block text-sm">Full name<input required maxLength={120} className="w-full border rounded p-2 mt-1" value={guestForm.fullName} onChange={e=>setGuestForm(p=>({...p,fullName:e.target.value}))}/></label>
            <label className="block text-sm">Email<input required type="email" maxLength={254} className="w-full border rounded p-2 mt-1" value={guestForm.email} onChange={e=>setGuestForm(p=>({...p,email:e.target.value}))}/></label>
            <label className="block text-sm">Phone<input required type="tel" maxLength={40} className="w-full border rounded p-2 mt-1" value={guestForm.phone} onChange={e=>setGuestForm(p=>({...p,phone:e.target.value}))}/></label>
            {guestEvent.requireInviteCode && <label className="block text-sm">Invite code<input required maxLength={100} className="w-full border rounded p-2 mt-1" value={guestForm.inviteCode} onChange={e=>setGuestForm(p=>({...p,inviteCode:e.target.value}))}/></label>}
            <p className="text-xs text-gray-500">Your details will be used to manage this event registration and send event updates.</p>
            <div className="flex gap-2 justify-end"><button type="button" className="px-4 py-2 border rounded" onClick={()=>setGuestEvent(null)} disabled={guestBusy}>Cancel</button><button type="submit" className="px-4 py-2 rounded bg-indigo-600 text-white" disabled={guestBusy}>{guestBusy?'Submitting...':'Confirm RSVP'}</button></div>
          </form>
        </div>
      )}

      {authPromptOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-6 mx-4">
            <h3 className="text-lg font-semibold mb-2 text-slate-600">
              Authentication Required
            </h3>
            <p className="text-sm text-slate-600 mb-4">{authPromptMessage}</p>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={() => {
                  closeAuthPrompt();
                  navigate("/login");
                }}
                className="px-4 py-2 rounded border border-slate-300 text-sm"
              >
                Login
              </button>

              <button
                onClick={() => {
                  closeAuthPrompt();
                  navigate("/signup");
                }}
                className="px-4 py-2 rounded bg-indigo-600 text-white text-sm"
              >
                Sign Up
              </button>
            </div>

            <button
              onClick={closeAuthPrompt}
              className="mt-3 w-full text-center text-xs text-slate-400"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </>
  );
}